import React, { useState } from "react";
import Button from "components/Button";
import useAnalytics from "hooks/useAnalytics";

const SignUp = ({
  headerText,
  subHeaderText,
  onSignupComplete,
  setOutsetaToken,
  setSignupEmail,
  auth0flags,
  slug,
  signupSource,
}) => {
  const [email, setEmail] = useState("");
  const [isAlreadySignedUp, setIsAlreadySignedUp] = useState(false);
  const [hasSignupError, setHasSignupError] = useState(false);
  const [hasEmailError, setHasEmailError] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isDisabled, setIsDisabled] = useState(email === "");
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const { track } = useAnalytics();

  const isEmail = (email) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

  const addNewCustomerAndAccount = async () => {
    // document.querySelector("#signup-submit").disabled = true;
    setIsDisabled(true);
    setIsSignupLoading(true);
    const emailExists = await doesEmailExist();

    if (emailExists === false) {
      const outsetaHeaders = new Headers();
      outsetaHeaders.append("Content-Type", "application/json");

      const outsetaResponse = await fetch(`/api/outseta-signup-plan-auth`, {
        method: "POST",
        body: JSON.stringify({
          firstName: firstName,
          lastName: lastName,
          email: email,
          slug: slug,
        }),
        headers: outsetaHeaders,
      });

      if (outsetaResponse.ok) {
        const tokenObj = await outsetaResponse.json();

        const reqHeaders = new Headers();
        reqHeaders.append("Content-Type", "application/json");

        // simulataneously authenticate outseta?
        const checkCreateResponse = await fetch(
          `/api/auth/create-auth0-account`,
          {
            method: "POST",
            body: JSON.stringify({
              email: email,
              name: `${firstName} ${lastName}`,
              auth0flags: auth0flags,
            }),
            headers: reqHeaders,
          }
        );

        fetch(`/api/background/post-signup`, {
          method: "POST",
          body: JSON.stringify({
            email: email,
            firstName: firstName,
            lastName: lastName,
            planName: "Workspace Account",
            uid: tokenObj?.Uid,
          })
        });

        if (checkCreateResponse.ok) {
          setSignupEmail(email);
          setOutsetaToken(tokenObj?.access_token);
          setIsSignupLoading(false);
          track("sign_up", {
            signup_source: signupSource,
          });
          if (slug) {
            track("case_followed", { slug: slug });
          }
          onSignupComplete();
          // console.log(`tokenObj: ${tokenObj}`);
        } else {
          setHasSignupError(true);
          const errorResponse = await checkCreateResponse.json();
          console.error(errorResponse);
        }
      } else {
        setIsDisabled(false);
        setIsSignupLoading(false);
        setHasSignupError(true);
      }
    } else {
      setIsSignupLoading(false);
    }
  };

  const doesEmailExist = async () => {
    setIsDisabled(true);
    setIsSignupLoading(true);

    const reqHeaders = new Headers();
    reqHeaders.append("Content-Type", "application/json");

    const checkEmailResponse = await fetch(`/api/auth/check-for-email`, {
      method: "POST",
      body: JSON.stringify({
        email: email,
      }),
      headers: reqHeaders,
    });

    if (checkEmailResponse.ok) {
      const response = await checkEmailResponse.json();
      if (response?.exists === true) {
        setIsAlreadySignedUp(true);
        setIsDisabled(true);
        return true;
      } else if (response?.exists === false) {
        return false;
      } else {
        console.error(response);
        setHasSignupError(true);
        setIsDisabled(false);
        setIsSignupLoading(false);
        return true;
      }
    } else {
      setHasSignupError(true);
      setIsDisabled(false);
      setIsSignupLoading(false);
      const errorResponse = await checkEmailResponse.json();
      console.error(errorResponse);
    }
  };

  const handleButtonClick = () => {
    // console.log("click.");
  };

  const handleEmailChange = (evt) => {
    setEmail(evt.target.value);
    setIsDisabled(evt.target.value === "");
    if (isAlreadySignedUp) {
      setIsAlreadySignedUp(false);
    }
    if (hasEmailError) {
      setHasEmailError(false);
    }
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    if (isEmail(email)) {
      addNewCustomerAndAccount();
    } else {
      setHasEmailError(true);
    }
  };

  return (
    <>
      <form className="text-center w-full" onSubmit={handleSubmit}>
        <h3 className="font-semibold mb-8 mt-16 text-xl">{headerText}</h3>
        <p className="mb-8 md:hidden mx-auto">{subHeaderText}</p>
        <div className="flex mt-8 flex-col gap-2 sm:flex-row sm:gap-4">
          <input
            autoFocus
            className="border min-w-0	 p-2 text-sm w-full"
            onChange={(evt) => setFirstName(evt.target.value)}
            type="text"
            placeholder="First Name"
            value={firstName}
          />
          <input
            className="border min-w-0	 p-2 text-sm w-full"
            onChange={(evt) => setLastName(evt.target.value)}
            type="text"
            placeholder="Last Name"
            value={lastName}
          />
        </div>
        <input
          autoFocus
          className="border min-w-0	mt-2 p-2 text-sm w-full"
          placeholder="Email"
          type="email"
          value={email}
          onChange={(evt) => handleEmailChange(evt)}
        />
        <Button
          className="mt-8 text-sm w-full"
          disabled={isDisabled}
          emphasis="primary"
          height="40"
          onClick={() => handleButtonClick()}
        >
          Sign Up
          {isSignupLoading && (
            <i className="animate-spin rounded-full h-4 w-4 ml-4 border-b-2 border-white-900" />
          )}
        </Button>
        {isAlreadySignedUp && (
          <>
            <p className="my-4 font-semibold text-red-500 text-xs">
              Looks like you might have an account already!
            </p>
            <p className="my-4 font-semibold text-red-500 text-xs">
              Try logging in at the link below.
            </p>
            <p className="my-4 font-semibold text-red-500 text-xs">
              Still having trouble? Contact{" "}
              <a
                className="font-semibold text-red hover:text-red-700"
                href="mailto:support@uncovered.com"
              >
                support@uncovered.com
              </a>{" "}
            </p>
          </>
        )}
        {hasEmailError && (
          <>
            <p className="my-4 text-center font-semibold text-red-500 text-xs">
              Please enter a valid email address.
            </p>
          </>
        )}
        {hasSignupError && (
          <>
            <p className="my-4 text-center font-semibold text-red-500 text-xs">
              There was a problem signing you up.
            </p>
            <p className="my-4 text-center font-semibold text-red-500 text-xs">
              You can try again or contact{" "}
              <a
                className="font-semibold text-red hover:text-red-700"
                href="mailto:support@uncovered.com"
              >
                support@uncovered.com
              </a>{" "}
            </p>
          </>
        )}
        <p className="my-4 text-center text-gray-500 text-xs">
          Already have an account?{" "}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            className="text-yellow font-semibold hover:text-yellow-500"
            href="/api/auth/login"
          >
            Log in here
          </a>
        </p>
        <small className="block mt-12 text-center text-gray-500 text-xs">
          By continuing you confirm that you&apos;ve read and accepted our{" "}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}          
          <a
            className="text-yellow font-semibold hover:text-yellow-500"
            href="/terms-of-service/"
          >
            terms of service
          </a>
        </small>
      </form>
    </>
  );
};

export default SignUp;
